import * as Sentry from "@sentry/browser";

import {
  getUser,
  saveUser,
  urlParams,
  uuidV4,
  wrappedGet,
  wrappedPost,
} from "./utils";
import { showToast } from "@sourcewhale/shared/src/svelte/utils/toast.js";
import { addWebsocketListener } from "@sourcewhale/shared/src/assets/js/src/general/websocket.js";
import { signOut } from "../../../../svelte/api/signout.js";
import { showDisconnected } from "../../../../svelte/src/elements/Banners/CrmDisconnect/utils.svelte";
import { showTeamOverLicenseLimitBanner } from "../../../../svelte/src/elements/Banners/TeamOverLicenseLimit/utils.svelte";

import { SIGNIN_PAGES } from "@sourcewhale/shared/src/assets/js/src/general/constants.js";
import { userStore } from "@sourcewhale/shared/src/svelte/api-stores/user-store.js";
import { showTrialEndingBanner } from "../../../../svelte/src/elements/Banners/TrialEnding/utils.svelte";

const { pathname, search } = window.location;

const CREDITS = {};

export const getCredits = (creditType) => {
  if (creditType in CREDITS) {
    return CREDITS[creditType];
  }
  throw new Error(`Invalid credit type: ${creditType}`);
};

const handleRestrictedAccount = (res) => {
  if (res.isTest && ["/projects", "/project"].includes(pathname)) {
    const HIDE_TOP_BAR = {
      "/projects": [
        "statusButtons",
        "viewActiveBtn",
        "viewArchivedBtn",
        "projectsTable_filter",
        "projectsUserSelector",
      ],
      "/project": [
        "projectTagSelector",
        "projectCampaignSelector",
        "projectStageSelector",
        "projectButtonsDiv",
      ],
    };
    HIDE_TOP_BAR[pathname].forEach((x) => {
      $(`#${x}`).hide();
    });
  }

  // TODO: Ultimately, on no page, should this be conditional on visibilities.js.
  if (pathname !== "/campaign") {
    $("#top-container").removeClass("skeleton-loading");
  }

  if (!(res.isTest || res.isExtensionOnly)) return;

  const ALLOWED_PAGES = [
    "/admin",
    "/contacts",
    "/integrations",
    "/options",
    "/project",
    "/projects",
    "/review",
    "/signin",
    "/upload",
  ];
  if (res.isTest) {
    ALLOWED_PAGES.push("/outreach");
  }
  if (!ALLOWED_PAGES.includes(pathname)) window.location.replace("/contacts");

  if (res.isTest || res.isExtensionOnly) {
    const HIDE_PROFILE_OPTIONS = ["profileDropdown", "unsubscribesDropdown"];
    if (res.isTest) {
      HIDE_PROFILE_OPTIONS.push(
        "extensionDropdown",
        "trainingDropdown",
        "featureRequestDropdown",
      );
    }
    HIDE_PROFILE_OPTIONS.forEach((x) => {
      $(`#${x}`).hide();
    });
  }
};

const displayTeamGroups = (user) => {
  let groups = user.teamGroups;

  const compareGroupNames = (a, b) =>
    a.groupName.toLowerCase().localeCompare(b.groupName.toLowerCase());

  if (!groups) return;
  groups = Object.entries(groups).map(([groupId, v]) => ({ groupId, ...v }));
  groups = groups.sort((a, b) => compareGroupNames(a, b));

  const page = window.location.pathname;

  let showGroup;
  if (groups.length > 0) {
    $("#teamGroupSelectorWrapper").show();
    const teamGroupSelector = $("#teamGroupSelector");
    teamGroupSelector.find("option").remove();

    if (page === "/admin")
      teamGroupSelector.append(
        $("<option>", { value: "_none", text: "No Group" }),
      );
    else
      teamGroupSelector.append(
        $("<option>", { value: "_all", text: "All Users" }),
      );

    groups.forEach((g) => {
      const opts = { value: g.groupId, text: g.groupName };
      teamGroupSelector.append($("<option>", opts));
      if (g.users.includes(user.userId)) {
        showGroup = g.groupId;
      }
    });

    const reportGroupSelector = $("#candidateStatsGroupBySelector");

    if (reportGroupSelector.length) {
      reportGroupSelector.append(
        '<option value="teamGroups" data-content="Team Group">Group by: Group</option>',
      );
      reportGroupSelector.selectpicker("refresh");
    }

    if (teamGroupSelector.length) {
      teamGroupSelector.selectpicker("refresh");
    }

    if (["/dashboard", "/"].includes(page) && showGroup) {
      teamGroupSelector.selectpicker("val", showGroup);
    }
  }
};

const setTrainingProgress = (trainingProgress, userId) => {
  trainingProgress = trainingProgress || [];

  const markTaskComplete = (taskId, key) => {
    if (!trainingProgress.includes(taskId))
      wrappedPost({
        url: "/api/users/save",
        data: JSON.stringify({ userId, updates: { [key]: true } }),
      });
  };

  if (pathname === "/dashboard") {
    setTimeout(() => {
      markTaskComplete("understand-the-dashboard", "hasVisitedDashboard");
    }, 1000 * 10);
  }

  if (pathname === "/todo") {
    setTimeout(() => {
      markTaskComplete("navigating-todos-page", "hasVisitedTodos");
    }, 1000 * 10);
  }

  if (pathname === "/campaign") {
    $("#settings-nav-tab-link").click(() => {
      markTaskComplete(
        "navigate-campaign-settings",
        "hasVisitedCampaignSettings",
      );
    });
  }

  if (pathname === "/review") {
    markTaskComplete("upload-spreadsheet", "hasUploadedSpreadsheet");
  }
};

const setCreditsCounter = (credits, key, plan = undefined) => {
  const unlimitedDialler = key === "dialler" && plan === "unlimited";

  credits = unlimitedDialler ? "unlimited" : credits || 0;

  $(`#${key}CounterNavItem`).removeClass("d-none").addClass("d-inline-flex");

  const suffix = key === "dialler" ? "Minutes" : "Credits";
  $(`#${key}${suffix}Counter`).text(credits);

  const WARNINGS = { email: 10, phone: 10, dialler: 100 };

  const danger = !unlimitedDialler && credits < WARNINGS[key];

  $(`#${key}${suffix}CounterIcon`).addClass(
    danger ? "text-danger" : "text-warning",
  );
};

const displayDataProviders = (dataProviders) => {
  Object.entries(dataProviders || {}).forEach(([provider, item]) => {
    const id = uuidV4();
    $("#creditsContainer").append(/* html */ `
        <div
        id="${id}"
        class="d-inline-flex flex-column align-items-center user-select-none mx-2"
        data-toggle="tooltip"
        title="${item.title} Credits"
        >
          <span class="d-flex justify-content-center w-100 h-100" style="height: 1.8rem;">
            <img src="${item.icon}" style="width: 1rem; height: 1rem">
          </span>
          <span id="${provider}CreditsCounter" class="text-gray-600 font-weight-bold text-center" style="font-size: 0.6rem">${item.credits}</span>
        </div>`);
    $(`#${id}`)
      .tooltip("dispose")
      .tooltip({ title: `${item.title} Credits` });
  });
};

const addIntegrationDisconnectedAlert = (alert) => {
  const { text, link, isAdmin, providerTitle } = alert;
  const adminText = `Your ${providerTitle} has been deactivated. <a href=${link}>Click here</a> to reactivate it.`;

  showToast({
    title: "Integration disconnected!",
    subtitle: isAdmin ? adminText : text,
    type: "warning",
    timeout: 45000,
  });
};

const addPublicHolidayAlert = (alert) => {
  const { title, text, hash } = alert;
  showToast({
    title,
    subtitle: text,
    type: "info",
    timeout: 45000,
    id: `public-holiday-${hash}`,
  });

  $(`#toast-public-holiday-${hash} .toast-close`).click(() => {
    wrappedPost({
      url: "/api/users/dismiss_alert",
      data: JSON.stringify({ hash }),
    });
  });
};

export const initializeVisibilities = () => {
  console.log(`

   ___  ___  _   _ _ __ ___ ___
  / __|/ _ \\| | | | '__/ __/ _ \\
  \\__ \\ (_) | |_| | | | (_|  __/
  |___/\\___/ \\__,_|_|  \\___\\___|

       ":"
       _:______    |"\\ /"|
     ,'        '.    \\ /
     |  O        \\___/ |
  ~^~^~^~^~^~^~^~^~^~^~^~^~^~^~
             _           _
   __      _| |__   __ _| | ___
   \\ \\ /\\ / / '_ \\ / _' | |/ _ \\
    \\ V  V /| | | | (_| | |  __/
     \\_/\\_/ |_| |_|\\__,_|_|\\___|

    `);

  $("#userName").text(getUser().name);

  $("#userImg").attr("src", getUser().picture);

  if (urlParams.get("provider")) {
    localStorage.setItem("provider", urlParams.get("provider"));
  }

  if (!SIGNIN_PAGES.includes(pathname) && !localStorage.getItem("provider")) {
    window.location.replace(`/signin?redirect=${pathname}${search}`);
  }

  addWebsocketListener((data) => {
    if (data.kind === "showToast") {
      const title = data.title || "";
      const subtitle = data.subtitle || "";
      const timeout = data.timeout || 10000;
      const type = data.type || "info";

      showToast({
        title,
        subtitle,
        timeout,
        type,
      });
    }

    if (data.kind === "hardRefresh") {
      window.location.reload(true);
    }

    if (data.kind === "getNewCookies") {
      wrappedGet({
        url: "/api/signin/get_new_cookies",
        success: () => {
          window.location.reload(true);
        },
      });
    }

    if (data.kind === "signOut") {
      signOut(data.toast);
    }

    if (data.kind === "update-teams-credit-history") {
      data.changes.forEach((change) => {
        ["emailCredits", "phoneCredits"].forEach((key) => {
          $(`#${key}Counter`).text(change.update[key]);
        });

        if (getUser().diallerPlan === "standard") {
          $(`#diallerMinutesCounter`).text(change.update.diallerMinutes);
        }
      });
    }

    if (data.kind === "fileChange") {
      // hot reloading
      if (data.filename.endsWith(".css")) {
        const queryString = `?reload=${Date.now()}`;
        $('link[rel="stylesheet"]').each((_, el) => {
          const href = $(el).attr("href");
          if (href.split("?")[0] === data.filename)
            $(el).attr("href", href.replace(/\?.*|$/, queryString));
        });
      }
      if (data.filename.endsWith(".js")) {
        $("script").each((_, el) => {
          const src = $(el).attr("src");
          if (src.split("?")[0] === data.filename) window.location.reload(true);
        });
      }
      if (data.filename.endsWith(".html")) {
        if (window.location.pathname === data.filename.split(".html")[0])
          window.location.reload(true);
      }
    }
  });

  userStore.once().then((res) => {
    // auth errors are caught upstream causing once() to return null.
    // execution *can* continue before the redirect, so return here.
    if (!res) return;

    if (res.licenseKindsOverLimit) {
      showTeamOverLicenseLimitBanner(res.licenseKindsOverLimit);
    }

    $(".logo").attr("href", "/");
    if (SIGNIN_PAGES.includes(pathname) && localStorage.getItem("provider")) {
      if (urlParams.get("stopRedirect") !== "true") {
        window.location.replace("/");
      }
    }

    saveUser(res);
    handleRestrictedAccount(res);

    if (!SIGNIN_PAGES.includes(pathname))
      localStorage.setItem("provider", res.provider);

    $("#userName").text(res.name);
    $("#userImg").attr("src", res.picture);

    const scope = Sentry.getCurrentScope();
    scope.setUser({ id: res.userId, username: res.name, email: res.email });
    scope.setTag("teamId", res.teamId);

    if (res.isInternal) {
      $("#internalTickets").show();
    }

    displayTeamGroups(res);

    if (res.isOnAnotherTeam) {
      showToast({
        title: `You are currently viewing ${res.teamName}`,
        type: "warning",
        timeout: 30000,
      });
    }

    setTrainingProgress(res.trainingProgress, res.userId);

    ["email", "phone"].forEach((key) => {
      if (res.searchKinds[key]) {
        const uncapped = pathname === "/admin" ? "Uncapped" : "";
        setCreditsCounter(res[`${key}Credits${uncapped}`], key);
        CREDITS[`${key}Credits`] = res[`${key}Credits`];
      }
    });

    if (["standard", "unlimited"].includes(res.diallerPlan)) {
      setCreditsCounter(res.diallerMinutes, "dialler", res.diallerPlan);
    }

    displayDataProviders(res.dataProviders);
  });

  $("#adminDropdownDiv").toggle(!!getUser().isAdmin);

  if (urlParams.get("isAdmin") === "1") $("#adminDropdownDiv").show();

  wrappedGet({
    url: "/api/users/get_alerts",
    success: (res) => {
      if (res.length > 0) {
        $("#alertsCounterSpan").show();
        $("#alertsLink").removeClass("d-none");
      }

      res.forEach((alert) => {
        if (alert.kind === "integrationDisconnected")
          addIntegrationDisconnectedAlert(alert);
        if (alert.kind === "publicHoliday") addPublicHolidayAlert(alert);
        if (alert.kind === "plgTrialEnding") showTrialEndingBanner();
      });

      const crmAlerts = res.filter(
        (alert) => alert.kind === "individualIntegrationDisconnected",
      );
      showDisconnected(crmAlerts);
    },
  });
};
